<script lang="ts">
  import { onMount, type Snippet } from 'svelte';
  import { fade } from 'svelte/transition';
  import { page } from '$app/state';
  import UserMenu from './UserMenu.svelte';
  import Toast from './Toast.svelte';
  import BuildInfoPill from './BuildInfoPill.svelte';
  import RequestProgress from './RequestProgress.svelte';
  import { identity as identityStore } from '$lib/state/identity.svelte';
  import { apiClient } from '$lib/api/client';
  import { toasts } from '$lib/state/toast.svelte';
  import { theme } from '$lib/state/theme.svelte';
  import { popover } from '$lib/a11y/dismissible';

  type Crumb = { href?: string; label: string };
  let { children, breadcrumbs = [] } = $props<{ children: Snippet; breadcrumbs?: Crumb[] }>();
  let identity = $derived(identityStore.value);
  let inPreview = $derived(identity?.preview_origin !== undefined);
  // Presentation only: the server authorizes every request. Before identity
  // resolves nothing member-only is rendered, so a guest never sees (or
  // follows) a Users/Groups/Admin/About/API/What's new link it would be denied.
  let isMember = $derived(identity?.user_class === 'member');
  let isGuest = $derived(identity !== null && identity.user_class !== 'member');

  // Dev-env detection: prod console runs on console.vibe.mg, dev on
  // console.mgsec.dev. Anything else (localhost, preview hostnames) is
  // also treated as non-prod and gets the same warning chrome — better
  // to over-warn than to confuse a dev session for prod.
  let isDev = $state(false);
  onMount(() => {
    const host = window.location.hostname;
    isDev = host !== 'console.vibe.mg';
    theme.init();
  });

  // Mobile nav drawer state. Auto-closes whenever the route changes so
  // tapping a link in the drawer dismisses it as the new page loads.
  let mobileNavOpen = $state(false);
  let currentPath = $derived(page.url.pathname);
  $effect(() => {
    currentPath;
    mobileNavOpen = false;
  });

  // Stable "section" key for the transition wrapper. Most pages key on the
  // full pathname so the in:fade fires on every nav. But layouts that own
  // their own shell (and their own internal fade between sub-tabs) need
  // their chrome to stay mounted across sub-route navigations. For those
  // we collapse the key to the layout's mount-point so only the inner
  // shell decides when to fade.
  let sectionKey = $derived.by(() => {
    const path = page.url.pathname;
    const appMatch = path.match(/^(\/a\/[^/]+)(\/|$)/);
    if (appMatch) return appMatch[1];
    return path;
  });

  function isActive(href: string): boolean {
    const path = page.url.pathname;
    if (href === '/') return path === '/' || path.startsWith('/a/');
    return path.startsWith(href);
  }

  function navClass(href: string, mobile = false): string {
    const active = isActive(href);
    if (mobile) return `px-4 py-3 hover:bg-zinc-50${active ? ' font-semibold text-zinc-900' : ''}`;
    return `hover:underline${active ? ' font-semibold text-zinc-900' : ''}`;
  }

  let exiting = $state(false);
  async function exitPreview(): Promise<void> {
    exiting = true;
    try {
      await apiClient.exitPreviewSession();
      // Cookies were rewritten by the server. Hard-reload so /api/v1/me
      // re-runs and the layout picks up the admin identity again.
      window.location.assign('/admin/preview-users');
    } catch (err) {
      exiting = false;
      toasts.push(
        'error',
        `Failed to exit preview: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }
</script>

<div class="min-h-screen flex flex-col {isDev ? 'border-4 border-red-300' : ''}">
  <RequestProgress />
  {#if inPreview && identity}
    <div
      class="bg-violet-600 text-white text-sm px-4 py-2 flex items-center justify-between gap-3"
      role="status"
      aria-live="polite"
    >
      <div class="flex items-center gap-2">
        <span aria-hidden="true">🔭</span>
        <span>
          Preview mode — acting as
          <strong class="font-semibold">{identity.name}</strong>
          <span class="text-violet-200">({identity.email})</span>
        </span>
      </div>
      <button
        type="button"
        onclick={exitPreview}
        disabled={exiting}
        class="text-xs uppercase tracking-wide font-semibold bg-white/15 hover:bg-white/25 disabled:opacity-50 px-3 py-1 rounded transition"
      >
        {exiting ? 'Exiting…' : 'Exit preview'}
      </button>
    </div>
  {/if}
  {#if isGuest}
    <div
      class="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-4 py-2"
      role="status"
      data-testid="guest-banner"
    >
      <strong class="font-semibold">Guest access</strong> — you can open only apps explicitly
      shared with you or your group, read-only. Deploying, tokens, MCP and directory pages are
      not available.
    </div>
  {/if}
  <header
    class="border-b px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-2 {isDev
      ? 'bg-red-50 border-red-200'
      : 'bg-white border-zinc-200'}"
  >
    <div class="flex items-center gap-3 sm:gap-6 min-w-0 flex-1">
      <button
        type="button"
        class="md:hidden inline-flex items-center justify-center w-9 h-9 -ml-1 rounded text-zinc-600 hover:bg-zinc-100"
        aria-label="Open navigation"
        aria-haspopup="menu"
        aria-expanded={mobileNavOpen}
        onclick={() => (mobileNavOpen = !mobileNavOpen)}
      >
        {#if mobileNavOpen}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        {:else}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        {/if}
      </button>
      <a href="/" class="font-semibold whitespace-nowrap">MG Vibes</a>
      {#if isDev}
        <span
          class="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded bg-red-600 text-white"
          title="You are on the dev console ({typeof window !== 'undefined'
            ? window.location.hostname
            : 'mgsec.dev'}). Actions here do not affect production."
        >
          DEV
        </span>
      {/if}
      <nav class="text-sm text-zinc-600 hidden md:flex gap-4">
        <a href="/" class={navClass('/')}>Apps</a>
        {#if isMember}
          <a href="/users" class={navClass('/users')}>Users</a>
          <a href="/groups" class={navClass('/groups')}>Groups</a>
          <a href="/about" class={navClass('/about')}>About</a>
          <a href="/api-docs" class={navClass('/api-docs')}>API</a>
        {/if}
        {#if isMember && identity?.platform_admin}
          <a href="/admin" class={navClass('/admin')}>Admin</a>
        {/if}
      </nav>
      {#if breadcrumbs.length > 0}
        <nav class="text-sm text-zinc-600 hidden lg:flex flex-wrap gap-x-2 gap-y-1 min-w-0">
          {#each breadcrumbs as crumb, i (crumb.label)}
            {#if i > 0}<span class="text-zinc-400">/</span>{/if}
            {#if crumb.href}<a class="hover:underline truncate max-w-[12rem]" href={crumb.href}
                >{crumb.label}</a
              >{:else}<span class="truncate max-w-[12rem]">{crumb.label}</span>{/if}
          {/each}
        </nav>
      {/if}
    </div>
    <div class="flex items-center gap-2 sm:gap-3 shrink-0">
      {#if isMember}
        <a
          href="/about/whats-new"
          class="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition shadow-sm"
          title="See what's new"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path
              d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.582a.5.5 0 0 1 0 .962L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"
            />
          </svg>
          <span>What's new</span>
        </a>
      {/if}
      <a
        href="https://matchgroup.enterprise.slack.com/archives/C0B3FK95WJ0"
        target="_blank"
        rel="noopener noreferrer"
        class="hidden sm:inline text-sm text-zinc-500 hover:text-zinc-900 transition whitespace-nowrap"
        title="Get help in #vibe-mg on Slack"
      >
        #vibe-mg
      </a>
      {#if identity}
        <UserMenu current={identity} />
      {:else}
        <!-- Placeholder while the root layout's identityPromise resolves;
             keeps the header's right-side width stable so the chrome
             doesn't jump when the menu pops in. -->
        <div class="h-7 w-20 sm:w-28 rounded bg-zinc-100 animate-pulse" aria-hidden="true"></div>
      {/if}
    </div>
  </header>
  {#if mobileNavOpen}
    <div
      class="md:hidden border-b border-zinc-200 bg-white shadow-sm"
      role="menu"
      aria-label="Mobile navigation"
      {@attach popover({ onClose: () => (mobileNavOpen = false), autoFocus: false })}
    >
      <nav class="flex flex-col text-sm divide-y divide-zinc-100">
        <a href="/" class={navClass('/', true)}>Apps</a>
        {#if isMember}
          <a href="/users" class={navClass('/users', true)}>Users</a>
          <a href="/groups" class={navClass('/groups', true)}>Groups</a>
          <a href="/about" class={navClass('/about', true)}>About</a>
          <a href="/api-docs" class={navClass('/api-docs', true)}>API</a>
        {/if}
        {#if isMember && identity?.platform_admin}
          <a href="/admin" class={navClass('/admin', true)}>Admin</a>
        {/if}
        {#if isMember}
          <a href="/about/whats-new" class="px-4 py-3 hover:bg-zinc-50 text-indigo-700">What's new</a>
        {/if}
        <a
          href="https://matchgroup.enterprise.slack.com/archives/C0B3FK95WJ0"
          target="_blank"
          rel="noopener noreferrer"
          class="px-4 py-3 hover:bg-zinc-50 text-zinc-500">#vibe-mg ↗</a
        >
        {#if breadcrumbs.length > 0}
          <div class="px-4 py-2 bg-zinc-50">
            <div class="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Location</div>
            <div class="flex flex-wrap gap-x-2 text-xs text-zinc-600">
              {#each breadcrumbs as crumb, i (crumb.label)}
                {#if i > 0}<span class="text-zinc-400">/</span>{/if}
                {#if crumb.href}<a class="hover:underline" href={crumb.href}>{crumb.label}</a
                  >{:else}<span>{crumb.label}</span>{/if}
              {/each}
            </div>
          </div>
        {/if}
      </nav>
    </div>
  {/if}
  <main class="flex-1 p-3 sm:p-6">
    <!--
      Subtle fade-in on route change. {#key page.url.pathname} forces the
      wrapper to remount when the path changes; in:fade does the entrance
      animation. No out: transition — old DOM exits instantly so the new
      content paints immediately rather than overlapping. 120ms is fast
      enough to feel snappy but slow enough to register as intentional.
    -->
    {#key sectionKey}
      <div in:fade={{ duration: 120 }}>
        {@render children()}
      </div>
    {/key}
  </main>
  <Toast />
  <BuildInfoPill />
</div>
